import withStyles from 'isomorphic-style-loader/withStyles'
import React, { useEffect } from 'react'
import { withTranslation } from "react-i18next"
import {BriefcaseIcon, ContainerIcon, DatabaseIcon, HomeIcon, OrganizationIcon, PackageIcon, PersonIcon} from '@primer/octicons-react'
import s from './Navigation.css'
import { useNavigationLogic, getAccountOptions, shouldShowMenu } from './Navigation.functions'
import NavigationMenu from './NavigationMenu'
import NavigationTools from './NavigationTools'
import NavigationAccount from './NavigationAccount'
import NavigationHousing from './NavigationHousing'
import NavigationReader from './NavigationReader'

/**
 * Composant de navigation principal
 * Affiche les menus de navigation selon le type d'utilisateur
 * 
 * @param {Object} props - Props du composant
 * @param {Object} props.me - Utilisateur connecté
 * @param {Object} props.context - Contexte de l'application
 * @param {Function} props.t - Fonction de traduction
 * @param {Function} props.emitEvent - Fonction d'émission d'événements
 * @returns {JSX.Element} Composant Navigation
 */
const Navigation = ({ me, context, t, emitEvent, ...props }) => {
  const {
    state,
    handleMenu,
    handleWindowClick,
    getIconTag,
    updateAlarmAsync,
    updateAutoMeterAsync,
    updateAutoTableAsync,
    testSendAlarmEmail,
    updateGeoLocation
  } = useNavigationLogic({ me, emitEvent, t });

  // Gestion des événements window
  useEffect(() => {
    window.addEventListener("click", handleWindowClick);
    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
  }, [handleWindowClick]);

  // Options de compte selon le type d'utilisateur
  const accountOptions = getAccountOptions(me, t, getIconTag);

  // Pour les utilisateurs lecteurs (type >= 40), bandeau simplifié
  if (me && me.type >= 40) {
    return (
      <div className={s.root} role="navigation">
        <NavigationReader
          me={me}
          menuOpened={state.menuOpened}
          handleMenu={handleMenu}
          getIconTag={getIconTag}
          t={t}
        />
      </div>
    )
  }

		return (
			<div className={s.root} role="navigation">
      {/* Menu Admin */}
      {shouldShowMenu('admin', me) && (
        <NavigationMenu
						menuKey="admin"
          menuOpened={state.menuOpened}
          title={{ icon: getIconTag(DatabaseIcon), label: t("navigation.admins") }}
						link="/admins"
          handleMenu={handleMenu}
        />
      )}

      {/* Menu Building Manager */}
      {shouldShowMenu('buildManager', me) && (
        <NavigationMenu
						menuKey="buildManager"
          menuOpened={state.menuOpened}
          title={{ icon: getIconTag(BriefcaseIcon), label: t("navigation.buildingmanagers") }}
          link={me.type < 20 ? "/buildingmanagers" : "/buildingmanager/" + me.user_group_id}
          handleMenu={handleMenu}
        />
      )}

      {/* Menu Housing Group */}
      {shouldShowMenu('user', me) && (
        <NavigationMenu
						menuKey="user"
          menuOpened={state.menuOpened}
          title={{ icon: getIconTag(PersonIcon), label: t("navigation.users") }}
          link="/users"
          handleMenu={handleMenu}
        />
      )}

      {/* Menu Housing - Spécialisé selon le type d'utilisateur */}
      <NavigationHousing
        me={me}
        menuOpened={state.menuOpened}
        getIconTag={getIconTag}
        handleMenu={handleMenu}
        t={t}
      />

      {/* Menu Tools géré par NavigationTools */}
      {shouldShowMenu('tools', me) && (
        <NavigationTools
          emitEvent={emitEvent}
          me={me}
          menuOpened={state.menuOpened}
          getIconTag={getIconTag}
          handleMenu={handleMenu}
          t={t}
          state={state}
          updateAlarmAsync={updateAlarmAsync}
          updateAutoMeterAsync={updateAutoMeterAsync}
          updateAutoTableAsync={updateAutoTableAsync}
          updateGeoLocation={updateGeoLocation}
        />
      )}

      {/* Menu Account */}
      <NavigationAccount
        me={me}
        menuOpened={state.menuOpened}
        accountOptions={accountOptions}
        handleMenu={handleMenu}
        getIconTag={getIconTag}
        t={t}
      />
			</div>
  );
};

export default withTranslation()(withStyles(s)(Navigation));
